import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { combineLatest } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { DropdownInfoConfig, DropdownOptionLike } from '../../../core/constants/api-endpoints';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';
import { AppUserProfile, AppUserService } from '../../../core/services';
import { AssignPackageRecord, SubscriptionPackage, SubscriptionService } from '../../../core/services/subscription.service';
import { CommonDropdownComponent } from '../../../core/ui/common-dropdown/common-dropdown.component';

export interface AssignPackageSaveEvent {
  mode: 'create' | 'edit';
  refId?: number;
}

@Component({
  selector: 'app-add-edit-assign-package',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe, CommonDropdownComponent, MatDatepickerModule, MatInputModule, MatNativeDateModule],
  templateUrl: './add-edit-assign-package.component.html',
  styleUrls: ['./add-edit-assign-package.component.css', './../../../../CSSFiles/BTN_Style.css']
})
export class AddEditAssignPackageComponent implements OnInit {
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() initialAssignment: AssignPackageRecord | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<AssignPackageSaveEvent>();

  assignForm: FormGroup;
  isSubmitting = false;
  isLoadingOptions = false;
  error: string | null = null;
  private shouldAutoSyncExpireDate = false;
  users: AppUserProfile[] = [];
  packages: SubscriptionPackage[] = [];
  matExpireDateValue: Date | null = null;
  packageSelectionId: number | string | null = null;
  readonly durationScaleMin = 1;
  readonly durationScaleMax = 36;
  packageDropdownObj: Partial<DropdownInfoConfig> = {
    name: 'packageSelection',
    valueKey: 'value',
    display1: 'label',
    label: 'assignPackage.package',
    placeholder: 'Select package',
    options: [],
    isSearchable: true,
    searchReadonly: true
  };
  durationDropdownObj: Partial<DropdownInfoConfig> = {
    name: 'durationUnite',
    valueKey: 'value',
    display1: 'label',
    label: 'assignPackage.duration',
    placeholder: 'Select duration',
    options: [
      { value: 1, label: 'Monthly' },
      { value: 2, label: 'Yearly' },
      { value: 3, label: 'Custom' }
    ],
    isSearchable: true,
    searchReadonly: true
  };
  userDropdownObj: Partial<DropdownInfoConfig> = {
    name: 'userId',
    valueKey: 'id',
    display1: 'displayName',
    display2: 'email',
    label: 'assignPackage.user',
    placeholder: 'Select user',
    options: [],
    isSearchable: true
  };

  constructor(
    private readonly fb: FormBuilder,
    private readonly appUserService: AppUserService,
    private readonly subscriptionService: SubscriptionService
  ) {
    this.assignForm = this.fb.group({
      userId: [null, Validators.required],
      packageName: ['', Validators.required],
      price: [0, [Validators.required, Validators.min(0)]],
      maxActiveFlat: [0, [Validators.required, Validators.min(0)]],
      maxActiveRenter: [0, [Validators.required, Validators.min(0)]],
      maxActiveEmployee: [0, [Validators.required, Validators.min(0)]],
      maxFileStorage: [0, [Validators.required, Validators.min(0)]],
      isTrial: [false],
      remarks: [''],
      durationUnite: [1, Validators.required],
      durationQuantity: [1, [Validators.required, Validators.min(1)]],
      expireDate: [''],
      status: [1]
    });
  }

  ngOnInit(): void {
    this.loadOptions();

    if (this.mode === 'edit' && this.initialAssignment) {
      this.assignForm.patchValue({
        userId: this.initialAssignment.userId ?? null,
        packageName: this.initialAssignment.packageName ?? '',
        price: this.initialAssignment.price ?? 0,
        maxActiveFlat: this.initialAssignment.maxActiveFlat ?? 0,
        maxActiveRenter: this.initialAssignment.maxActiveRenter ?? 0,
        maxActiveEmployee: this.initialAssignment.maxActiveEmployee ?? 0,
        maxFileStorage: this.initialAssignment.maxFileStorage ?? 0,
        isTrial: this.initialAssignment.isTrial ?? false,
        remarks: this.initialAssignment.remarks ?? '',
        durationUnite: this.initialAssignment.durationUnite ?? 1,
        durationQuantity: this.initialAssignment.durationQuantity ?? 1,
        expireDate: this.initialAssignment.expireDate ?? '',
        status: this.initialAssignment.status ?? 1
      });
      this.matExpireDateValue = this.toDateFromApiValue(this.initialAssignment.expireDate);
      this.shouldAutoSyncExpireDate = false;
    } else {
      this.shouldAutoSyncExpireDate = true;
    }

    this.syncPriceControlState();

    this.assignForm.get('durationUnite')?.valueChanges.subscribe(() => {
      this.syncPackageSnapshot();
      this.syncPriceControlState();
      this.shouldAutoSyncExpireDate = true;
      this.syncExpireDateFromDuration();
    });
    this.assignForm.get('durationQuantity')?.valueChanges.subscribe(() => {
      this.shouldAutoSyncExpireDate = true;
      this.syncExpireDateFromDuration();
    });
    this.assignForm.get('isTrial')?.valueChanges.subscribe((isTrial) => {
      if (isTrial) {
        this.assignForm.patchValue({ price: 0 }, { emitEvent: false });
        this.syncPriceControlState();
        return;
      }
      this.syncPackageSnapshot();
      this.syncPriceControlState();
    });
  }

  increaseDurationQuantity(): void {
    const current = this.currentDurationQuantity();
    if (current >= this.durationScaleMax) {
      return;
    }

    this.assignForm.patchValue({
      durationQuantity: current + 1
    });
  }

  decreaseDurationQuantity(): void {
    const current = this.currentDurationQuantity();
    if (current <= this.durationScaleMin) {
      return;
    }

    this.assignForm.patchValue({
      durationQuantity: current - 1
    });
  }

  onDurationScaleInput(event: Event): void {
    const nextValue = Number((event.target as HTMLInputElement | null)?.value ?? this.durationScaleMin);
    this.assignForm.patchValue({
      durationQuantity: this.clampDurationQuantity(nextValue)
    });
  }

  durationQuantityDisplay(): number {
    return this.currentDurationQuantity();
  }

  closeModal(): void {
    if (!this.isSubmitting) {
      this.close.emit();
    }
  }

  hasError(controlName: string, errorKey?: string): boolean {
    const control = this.assignForm.get(controlName);
    if (!control || !control.touched) {
      return false;
    }
    return errorKey ? control.hasError(errorKey) : control.invalid;
  }

  clearZeroOnFocus(controlName: string): void {
    const control = this.assignForm.get(controlName);
    if (!control) {
      return;
    }

    const value = Number(control.value ?? 0);
    if (Number.isFinite(value) && value === 0) {
      control.setValue('', { emitEvent: false });
    }
  }

  userLabel(user: AppUserProfile): string {
    return `${user.organizationName ?? user.name ?? ''}`.trim() || `User #${user.id}`;
  }

  onUserChange(event: { value: unknown }): void {
    const userId = Number(event.value);
    this.assignForm.patchValue({
      userId: Number.isFinite(userId) ? userId : null
    });
  }

  onUserOptionsLoaded(event: { options: DropdownOptionLike[] }): void {
    const currentUserId = Number(this.assignForm.get('userId')?.value);
    if ((Number.isFinite(currentUserId) && currentUserId > 0) || event.options.length !== 1) {
      return;
    }

    const userId = Number(event.options[0]?.['id']);
    if (!Number.isFinite(userId)) {
      return;
    }

    this.assignForm.patchValue({ userId });
  }

  onExpireDateSelected(event: MatDatepickerInputEvent<Date>): void {
    const pickedDate = event.value;
    if (!pickedDate || Number.isNaN(pickedDate.getTime())) {
      return;
    }

    const year = pickedDate.getFullYear();
    const month = `${pickedDate.getMonth() + 1}`.padStart(2, '0');
    const day = `${pickedDate.getDate()}`.padStart(2, '0');
    this.assignForm.patchValue({
      expireDate: `${year}-${month}-${day}`
    });
    this.matExpireDateValue = new Date(year, pickedDate.getMonth(), pickedDate.getDate());
  }

  expireDateDisplay(): string {
    const raw = `${this.assignForm.get('expireDate')?.value ?? ''}`.trim();
    if (!raw) {
      return '';
    }

    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      return raw;
    }

    return `${match[3]}-${match[2]}-${match[1]}`;
  }

  onPackageSelectionChange(event: { value: unknown }): void {
    const nextValue = event.value;
    this.packageSelectionId = typeof nextValue === 'string' || typeof nextValue === 'number'
      ? nextValue
      : '';
    this.syncPackageSnapshot();
    this.syncExpireDateFromDuration();
  }

  onPackageOptionsLoaded(event: { options: DropdownOptionLike[] }): void {
    if (this.packageSelectionId !== null && this.packageSelectionId !== '') {
      return;
    }

    if (event.options.length !== 1) {
      return;
    }

    const packageId = Number(event.options[0]?.['value']);
    if (!Number.isFinite(packageId)) {
      return;
    }

    this.packageSelectionId = packageId;
    this.syncPackageSnapshot();
    this.syncExpireDateFromDuration();
  }

  onDurationChange(event: { value: unknown }): void {
    const durationUnite = Number(event.value ?? 1);
    this.assignForm.patchValue({
      durationUnite: Number.isFinite(durationUnite) ? durationUnite : 1
    });
  }

  isReadonlyForPackageFields(): boolean {
    return !this.isCustomPackage();
  }

  isPriceReadonly(): boolean {
    return this.assignForm.get('isTrial')?.value === true || this.isReadonlyForPackageFields();
  }

  onTrialToggle(event: Event): void {
    const checked = (event.target as HTMLInputElement | null)?.checked === true;
    this.assignForm.patchValue({ isTrial: checked });
  }

  isCustomPackage(): boolean {
    const selected = this.selectedPackage();
    return `${selected?.packageName ?? this.assignForm.get('packageName')?.value ?? ''}`.trim().toLowerCase() === 'custom';
  }

  totalPrice(): number {
    const price = Number(this.assignForm.getRawValue()?.price ?? 0);
    const quantity = Number(this.assignForm.getRawValue()?.durationQuantity ?? 0);
    return price * quantity;
  }

  formatPrice(value: number): string {
    return new Intl.NumberFormat('en-BD', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 2
    }).format(Number(value ?? 0));
  }

  onSubmit(): void {
    if (this.assignForm.invalid) {
      this.assignForm.markAllAsTouched();
      this.error = 'Please fix the highlighted fields.';
      return;
    }

    const raw = this.assignForm.getRawValue();
    const payload = {
      id: this.mode === 'edit' ? Number(this.initialAssignment?.id) : undefined,
      userId: Number(raw.userId),
      packageName: `${raw.packageName ?? ''}`.trim(),
      price: Number(raw.price ?? 0),
      maxActiveFlat: Number(raw.maxActiveFlat ?? 0),
      maxActiveRenter: Number(raw.maxActiveRenter ?? 0),
      maxActiveEmployee: Number(raw.maxActiveEmployee ?? 0),
      maxFileStorage: Number(raw.maxFileStorage ?? 0),
      isTrial: raw.isTrial === true,
      remarks: `${raw.remarks ?? ''}`.trim(),
      durationUnite: Number(raw.durationUnite ?? 1),
      durationQuantity: Number(raw.durationQuantity ?? 1),
      expireDate: raw.expireDate ? `${raw.expireDate}` : null,
      status: Number(raw.status ?? 1)
    };

    this.isSubmitting = true;
    this.error = null;

    const request$ = this.mode === 'edit'
      ? this.subscriptionService.updateAssignPackage({
        id: Number(payload.id),
        userId: payload.userId,
        packageName: payload.packageName,
        price: payload.price,
        maxActiveFlat: payload.maxActiveFlat,
        maxActiveRenter: payload.maxActiveRenter,
        maxActiveEmployee: payload.maxActiveEmployee,
        maxFileStorage: payload.maxFileStorage,
        isTrial: payload.isTrial,
        remarks: payload.remarks,
        durationUnite: payload.durationUnite,
        durationQuantity: payload.durationQuantity,
        expireDate: payload.expireDate,
        status: payload.status
      })
      : this.subscriptionService.createAssignPackage(payload);

    request$
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (res: any) => {
          if (`${res?.code ?? ''}` !== '000') {
            this.error = `${res?.message ?? `Failed to ${this.mode === 'edit' ? 'update' : 'create'} assigned package.`}`;
            return;
          }

          const refId = Number(res?.refId ?? this.initialAssignment?.id);
          this.save.emit({
            mode: this.mode,
            refId: Number.isFinite(refId) ? refId : undefined
          });
        },
        error: (err: any) => {
          this.error = err?.message || `Failed to ${this.mode === 'edit' ? 'update' : 'create'} assigned package.`;
        }
      });
  }

  private loadOptions(): void {
    this.isLoadingOptions = true;
    combineLatest([
      this.appUserService.listUsers({
        pagination: true,
        pageNo: 0,
        rowCount: 200,
        filters: { status: 1, role: 2 }
      }),
      this.subscriptionService.listPackages({
        pagination: true,
        pageNo: 0,
        rowCount: 200,
        filters: { status: 1 }
      })
    ]).pipe(
      finalize(() => (this.isLoadingOptions = false))
    ).subscribe({
      next: ([userRes, packageRes]) => {
        this.users = userRes.data || [];
        this.packages = packageRes.data || [];
        this.userDropdownObj = {
          ...this.userDropdownObj,
          options: this.users.map((user) => ({
            id: Number(user.id),
            displayName: this.userLabel(user),
            email: `${user.email ?? ''}`.trim()
          } satisfies DropdownOptionLike))
        };
        this.packageDropdownObj = {
          ...this.packageDropdownObj,
          options: this.packages.map((item) => ({
            value: Number(item.id),
            label: `${item.packageName ?? ''}`.trim()
          }))
        };
        this.initPackageSelection();
        this.syncPackageSnapshot();
        if (this.shouldAutoSyncExpireDate) {
          this.syncExpireDateFromDuration();
        }
      },
      error: (err: any) => {
        this.error = err?.message || 'Failed to load assign package options.';
      }
    });
  }

  private initPackageSelection(): void {
    const currentName = `${this.assignForm.get('packageName')?.value ?? ''}`.trim().toLowerCase();
    if (!currentName) {
      const firstPackage = this.packages[0];
      this.packageSelectionId = firstPackage ? Number(firstPackage.id) : null;
      return;
    }

    const selectedPackage = this.packages.find((item) => `${item.packageName ?? ''}`.trim().toLowerCase() === currentName);
    this.packageSelectionId = selectedPackage ? Number(selectedPackage.id) : null;
  }

  private selectedPackage(): SubscriptionPackage | undefined {
    const packageId = Number(this.packageSelectionId ?? 0);
    return this.packages.find((item) => Number(item.id) === packageId);
  }

  private syncPackageSnapshot(): void {
    const selectedPackage = this.selectedPackage();
    if (!selectedPackage) {
      return;
    }

    const durationUnite = Number(this.assignForm.get('durationUnite')?.value ?? 1);
    if (!this.isCustomPackage()) {
      this.assignForm.patchValue({
        packageName: selectedPackage.packageName,
        maxActiveFlat: Number(selectedPackage.totalFlat ?? 0),
        maxActiveRenter: Number(selectedPackage.totalRenter ?? 0),
        maxActiveEmployee: Number(selectedPackage.maxActiveEmployee ?? 0),
        maxFileStorage: Number(selectedPackage.maxFileMemory ?? 0),
        price: this.assignForm.get('isTrial')?.value === true ? 0 : (durationUnite === 2 ? Number(selectedPackage.priceYearly ?? 0) : Number(selectedPackage.priceMonthly ?? 0))
      }, { emitEvent: false });
      this.syncPriceControlState();
      return;
    }

    this.assignForm.patchValue({
      packageName: selectedPackage.packageName
    }, { emitEvent: false });
    this.syncPriceControlState();
  }

  private syncExpireDateFromDuration(): void {
    if (!this.shouldAutoSyncExpireDate) {
      return;
    }

    const durationUnite = Number(this.assignForm.get('durationUnite')?.value ?? 0);
    const durationQuantity = this.currentDurationQuantity();
    if (!Number.isFinite(durationQuantity) || durationQuantity <= 0) {
      return;
    }

    const baseDate = new Date();
    const expireDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
    if (durationUnite === 1) {
      expireDate.setMonth(expireDate.getMonth() + durationQuantity);
    } else if (durationUnite === 2) {
      expireDate.setFullYear(expireDate.getFullYear() + durationQuantity);
    } else {
      expireDate.setDate(expireDate.getDate() + durationQuantity);
    }

    const year = expireDate.getFullYear();
    const month = `${expireDate.getMonth() + 1}`.padStart(2, '0');
    const day = `${expireDate.getDate()}`.padStart(2, '0');
    this.assignForm.patchValue({
      expireDate: `${year}-${month}-${day}`
    }, { emitEvent: false });
    this.matExpireDateValue = new Date(year, expireDate.getMonth(), expireDate.getDate());
  }

  private toDateFromApiValue(value: string | null | undefined): Date | null {
    const raw = `${value ?? ''}`.trim();
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }

    return parsed;
  }

  private currentDurationQuantity(): number {
    const raw = Number(this.assignForm.get('durationQuantity')?.value ?? this.durationScaleMin);
    return this.clampDurationQuantity(raw);
  }

  private clampDurationQuantity(value: number): number {
    if (!Number.isFinite(value)) {
      return this.durationScaleMin;
    }

    return Math.min(this.durationScaleMax, Math.max(this.durationScaleMin, Math.round(value)));
  }

  private syncPriceControlState(): void {
    const priceControl = this.assignForm.get('price');
    if (!priceControl) {
      return;
    }

    if (this.isPriceReadonly()) {
      priceControl.disable({ emitEvent: false });
      return;
    }

    priceControl.enable({ emitEvent: false });
  }
}
